import React, { useEffect, useRef } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';

export const OnboardingTour = ({ run, onComplete }) => {
  const { user } = useAuth();
  const driverRef = useRef(null);
  const isViewer = user?.role === 'viewer';

  useEffect(() => {
    if (!run) return;

    // Define tour steps based on role
    const getSteps = () => {
      const baseSteps = [
        {
          popover: {
            title: '¡Bienvenido a FinScope! 🎉',
            description: 'Esta plataforma te ayudará a visualizar y controlar las finanzas de tu empresa desde un solo lugar. Te guiaremos por las principales secciones.',
            position: 'center'
          }
        },
        {
          element: '[data-testid="sidebar"]',
          popover: {
            title: 'Menú de Navegación',
            description: 'Aquí encontrarás todos los módulos de la plataforma: Dashboard, Movimientos, Pagos, Prioridades y Rentabilidad.',
            position: 'right'
          }
        },
        {
          element: '[data-testid="nav-dashboard"]',
          popover: {
            title: 'Dashboard',
            description: 'El Dashboard muestra una visión general del estado financiero de tu empresa, incluyendo ingresos, gastos y utilidades del período.',
            position: 'right'
          }
        }
      ];

      // Admin-only steps
      const adminSteps = [
        {
          element: '[data-testid="nav-movimientos"]',
          popover: {
            title: 'Movimientos',
            description: 'Aquí puedes cargar y gestionar los movimientos financieros de la empresa, ya sea manualmente o mediante archivos Excel.',
            position: 'right'
          }
        },
        {
          element: '[data-testid="nav-pagos"]',
          popover: {
            title: 'Pagos',
            description: 'Este módulo permite registrar y controlar pagos pendientes, fechas de vencimiento y obligaciones financieras.',
            position: 'right'
          }
        }
      ];

      const commonSteps = [
        {
          element: '[data-testid="nav-prioridades"]',
          popover: {
            title: 'Prioridades',
            description: 'Aquí puedes organizar los pagos por nivel de prioridad usando drag & drop para decidir qué pagar primero.',
            position: 'right'
          }
        },
        {
          element: '[data-testid="nav-rentabilidad"]',
          popover: {
            title: 'Rentabilidad',
            description: 'Este módulo te permite analizar la rentabilidad de cada cliente considerando ingresos, costos de equipo y herramientas.',
            position: 'right'
          }
        },
        {
          popover: {
            title: '¡Todo listo! ✅',
            description: 'Ahora estás listo para comenzar a usar FinScope. Puedes volver a ver este tour desde el botón de Ayuda (?) en cualquier momento.',
            position: 'center'
          }
        }
      ];

      if (isViewer) {
        return [...baseSteps, ...commonSteps];
      }

      return [...baseSteps, ...adminSteps, ...commonSteps];
    };

    // Initialize driver
    const driverObj = driver({
      showProgress: true,
      animate: true,
      allowClose: true,
      overlayColor: 'rgba(0, 0, 0, 0.7)',
      stagePadding: 10,
      stageRadius: 8,
      popoverClass: 'finscope-popover',
      progressText: 'Paso {{current}} de {{total}}',
      nextBtnText: 'Continuar →',
      prevBtnText: '← Anterior',
      doneBtnText: '¡Comenzar!',
      showButtons: ['next', 'previous'],
      steps: getSteps(),
      onDestroyStarted: async () => {
        try {
          await api.put('/usuarios/onboarding');
        } catch (error) {
          console.error('Error marking onboarding complete:', error);
        }
        onComplete?.();
        driverObj.destroy();
      }
    });

    driverRef.current = driverObj;
    driverObj.drive();

    return () => {
      if (driverRef.current) {
        driverRef.current.destroy();
      }
    };
  }, [run, isViewer, onComplete]);

  // Add custom styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .driver-popover.finscope-popover {
        background: white;
        border-radius: 12px;
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        max-width: 400px;
      }
      .driver-popover.finscope-popover .driver-popover-title {
        font-size: 1.25rem;
        font-weight: 700;
        color: #1e293b;
        font-family: 'Outfit', sans-serif;
      }
      .driver-popover.finscope-popover .driver-popover-description {
        color: #475569;
        font-size: 0.95rem;
        line-height: 1.6;
      }
      .driver-popover.finscope-popover .driver-popover-progress-text {
        color: #64748b;
        font-size: 0.75rem;
      }
      .driver-popover.finscope-popover button {
        border-radius: 8px;
        font-weight: 500;
        padding: 8px 16px;
        transition: all 0.2s;
      }
      .driver-popover.finscope-popover .driver-popover-next-btn {
        background: #2563eb;
        color: white;
      }
      .driver-popover.finscope-popover .driver-popover-next-btn:hover {
        background: #1d4ed8;
      }
      .driver-popover.finscope-popover .driver-popover-prev-btn {
        color: #475569;
      }
      .driver-popover.finscope-popover .driver-popover-prev-btn:hover {
        background: #f1f5f9;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return null;
};

export default OnboardingTour;
